import { useCallback, useEffect, useState } from 'react';
import type { ListProjectsQuery, ProjectDTO } from '@astral/shared';
import { api, ApiClientError } from '../lib/api.js';

interface UseProjectsResult {
  projects: ProjectDTO[];
  total: number;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Fetch the current user's projects for the given query. Re-fetches whenever
 * the serialized query changes; `reload()` forces a manual refresh.
 */
export function useProjects(query: Partial<ListProjectsQuery>): UseProjectsResult {
  const [projects, setProjects] = useState<ProjectDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const key = JSON.stringify(query);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .listProjects(query)
      .then((result) => {
        if (cancelled) return;
        setProjects(result.items);
        setTotal(result.total);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiClientError ? err.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { projects, total, loading, error, reload };
}
